import { useState, useRef, useEffect, useCallback } from "react";
import type React from "react";
import BASE_URL from "../../Config";

type Role = "user" | "assistant";

interface Message {
  id: string;
  role: Role;
  content: string;
  fileName?: string;
  timestamp: Date;
  streaming?: boolean;
}

interface AttachedFile {
  raw: File;
  name: string;
  size: number;
  isImage: boolean;
}

const CHAT_URL = `${BASE_URL}/vibecode-service/claude/chat`;
const FILE_URL = `${BASE_URL}/vibecode-service/claude/chat/file`;

const ANTHROPIC_MODELS = [
  { id: "claude-opus-4-6", label: "Claude Opus 4.6", desc: "Most capable" },
  { id: "claude-sonnet-4-5", label: "Claude Sonnet 4.5", desc: "Balanced" },
  { id: "claude-opus-4-0", label: "Claude Opus 4", desc: "Powerful" },
  { id: "claude-sonnet-4-0", label: "Claude Sonnet 4", desc: "Smart" },
];

const ACCEPTED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
];

const uid = () => Math.random().toString(36).slice(2, 9);
const fmtTime = (d: Date) =>
  d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
const fmtBytes = (b: number) =>
  b < 1024
    ? `${b} B`
    : b < 1048576
    ? `${(b / 1024).toFixed(1)} KB`
    : `${(b / 1048576).toFixed(1)} MB`;

// ── Core: extract text token from a raw SSE data string ──────────────────────
// Handles three formats your server might send:
//   1. Plain text:          data: Hello world
//   2. Anthropic streaming: data: {"type":"content_block_delta","delta":{"type":"text_delta","text":"Hello"}}
//   3. Simple JSON:         data: {"text":"Hello"} or data: {"content":"Hello"}
function extractToken(raw: string): string {
  // Use trimmed only for guards — preserve original spacing for plain text
  const trimmed = raw.trim();
  if (!trimmed || trimmed === "[DONE]") return "";

  // Try JSON parse first (Anthropic SSE or simple wrapper)
  if (trimmed.startsWith("{")) {
    try {
      const json = JSON.parse(trimmed);

      // Anthropic streaming format — text already has real \n chars
      if (json.type === "content_block_delta") {
        return json.delta?.text ?? "";
      }

      // Simple wrappers
      if (typeof json.text === "string") return json.text;
      if (typeof json.content === "string") return json.content;
      if (typeof json.delta === "string") return json.delta;

      // Non-text events (message_start, ping, message_stop, etc.) — skip
      return "";
    } catch {
      // Not JSON — fall through to plain text handling
    }
  }

  // Plain text token from server.
  // IMPORTANT: use `raw` (not trimmed) so leading/trailing newlines are kept.
  // Then unescape any backslash-escaped sequences the server may have encoded.
  return raw
    .replace(/\\n/g, "\n")
    .replace(/\\t/g, "\t")
    .replace(/\\r/g, "")
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
    .replace(/\\\\/g, "\\")
    .replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) =>
      String.fromCharCode(parseInt(hex, 16))
    );
}

async function streamChat(
  history: { role: string; content: string }[],
  model: string,
  onToken: (token: string) => void,
  signal: AbortSignal
): Promise<void> {
  const res = await fetch(`${CHAT_URL}?model=${encodeURIComponent(model)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(history),
    signal,
  });
  if (!res.ok) throw new Error(`Server error ${res.status}`);
  if (!res.body) throw new Error("No response body for streaming");

  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8", { fatal: false });
  let buffer = "";

  const processLine = (line: string) => {
    if (!line.startsWith("data:")) return;
    // Remove exactly one leading space after "data:" (SSE spec), keep the rest
    const raw = line.slice(5).replace(/^ /, "");
    const token = extractToken(raw);
    if (token) onToken(token);
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      const remaining = decoder.decode();
      if (remaining) buffer += remaining;
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split("\n\n");
    buffer = events.pop() ?? "";
    for (const event of events) {
      for (const line of event.split("\n")) {
        processLine(line);
      }
    }
  }

  // Flush remaining buffer
  if (buffer.trim()) {
    for (const line of buffer.split("\n")) {
      processLine(line);
    }
  }
}

async function streamChatWithFile(
  file: File,
  message: string,
  model: string,
  onToken: (token: string) => void,
  signal: AbortSignal
): Promise<void> {
  const form = new FormData();
  form.append("file", file);
  form.append("message", message);

  const res = await fetch(`${FILE_URL}?model=${encodeURIComponent(model)}`, {
    method: "POST",
    body: form,
    signal,
  });
  if (!res.ok) throw new Error(`Server error ${res.status}`);
  if (!res.body) throw new Error("No response body for file streaming");

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split("\n\n");
    buffer = events.pop() ?? "";
    for (const event of events) {
      for (const line of event.split("\n")) {
        if (!line.startsWith("data:")) continue;
        const raw = line.slice(5).replace(/^ /, "");
        const token = extractToken(raw);
        if (token) onToken(token);
      }
    }
  }
  if (buffer.trim()) {
    for (const line of buffer.split("\n")) {
      if (!line.startsWith("data:")) continue;
      const raw = line.slice(5).replace(/^ /, "");
      const token = extractToken(raw);
      if (token) onToken(token);
    }
  }
}

// ── Loader ────────────────────────────────────────────────────────────────────
function ThinkingLoader() {
  return (
    <div className="flex gap-3 items-start">
      <div
        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md"
        style={{ background: "linear-gradient(135deg,#6366f1,#8b5cf6)" }}
      >
        AI
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex items-center gap-3">
        <div className="flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-2 h-2 rounded-full bg-indigo-400 animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
        <span className="text-xs text-slate-400 font-medium">Thinking…</span>
      </div>
    </div>
  );
}

function StreamCursor() {
  return (
    <span
      className="inline-block w-[2px] h-[1em] bg-indigo-400 ml-0.5 align-middle"
      style={{ animation: "blink 0.8s step-end infinite" }}
    />
  );
}

// ── Markdown renderer ─────────────────────────────────────────────────────────
function stripBold(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1");
}

// Sanitize incomplete markdown during streaming (unpaired **)
function sanitizeMarkdown(text: string): string {
  const count = (text.match(/\*\*/g) || []).length;
  if (count % 2 !== 0) {
    const lastIdx = text.lastIndexOf("**");
    return text.slice(0, lastIdx) + text.slice(lastIdx + 2);
  }
  return text;
}

function renderInline(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const re = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*|\[([^\]]+)\]\(([^)]+)\))/g;
  let last = 0,
    m: RegExpExecArray | null,
    key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const raw = m[0];
    if (raw.startsWith("`"))
      parts.push(
        <code
          key={key++}
          className="bg-slate-100 text-indigo-600 rounded px-1 py-0.5 text-[12px] font-mono"
        >
          {raw.slice(1, -1)}
        </code>
      );
    else if (raw.startsWith("**"))
      parts.push(
        <strong key={key++} className="font-semibold text-slate-900">
          {raw.slice(2, -2)}
        </strong>
      );
    else if (raw.startsWith("*"))
      parts.push(
        <em key={key++} className="italic">
          {raw.slice(1, -1)}
        </em>
      );
    else if (raw.startsWith("["))
      parts.push(
        <a
          key={key++}
          href={m[3]}
          target="_blank"
          rel="noreferrer"
          className="text-indigo-600 underline underline-offset-2 hover:text-indigo-800"
        >
          {m[2]}
        </a>
      );
    last = m.index + raw.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function startsWithEmoji(s: string): boolean {
  const trimmed = s.trim();
  if (!trimmed) return false;
  const cp = trimmed.codePointAt(0);
  if (cp === undefined) return false;
  return (
    (cp >= 0x1f300 && cp <= 0x1faff) || // Misc symbols, emoticons, transport, etc.
    (cp >= 0x2600 && cp <= 0x27bf) ||   // Misc symbols & dingbats
    (cp >= 0xfe00 && cp <= 0xfe0f) ||   // Variation selectors
    cp === 0x200d                         // ZWJ
  );
}

// A proper markdown table row: starts AND ends with |, and has 2+ cells
function isTableRow(s: string): boolean {
  const t = s.trim();
  if (!t.startsWith("|") || !t.endsWith("|")) return false;
  return t.split("|").length - 2 >= 2;
}

function renderCodeBlock(lang: string, code: string, key: string) {
  return (
    <div key={key} className="my-3 rounded-xl overflow-hidden border border-slate-200">
      {lang && (
        <div className="bg-slate-100 px-3 py-1 text-[10px] font-mono text-slate-500 border-b border-slate-200 uppercase tracking-wider">
          {lang}
        </div>
      )}
      <pre className="bg-slate-950 text-slate-100 text-[12px] font-mono p-4 overflow-x-auto leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function MarkdownContent({
  content,
  streaming,
}: {
  content: string;
  streaming?: boolean;
}) {
  const safeContent = streaming ? sanitizeMarkdown(content) : content;
  const lines = safeContent.split("\n");
  const nodes: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Blank line
    if (trimmed === "") {
      nodes.push(<div key={`bl-${i}`} className="h-2" />);
      i++;
      continue;
    }

    // Fenced code block
    if (trimmed.startsWith("```")) {
      const lang = trimmed.slice(3).trim();
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        codeLines.push(lines[i]);
        i++;
      }
      nodes.push(renderCodeBlock(lang, codeLines.join("\n"), `code-${i}`));
      i++;
      continue;
    }

    // Horizontal rule
    if (/^---+$/.test(trimmed)) {
      nodes.push(<hr key={`hr-${i}`} className="my-3 border-slate-200" />);
      i++;
      continue;
    }

    // Headings
    const hMatch = trimmed.match(/^(#{1,4})\s+(.+)/);
    if (hMatch) {
      const level = hMatch[1].length;
      const cls =
        level === 1 ? "text-[15px] font-bold text-slate-900 mt-4 mb-1.5" :
        level === 2 ? "text-[14px] font-bold text-slate-800 mt-3 mb-1" :
                      "text-[13.5px] font-semibold text-slate-700 mt-2 mb-0.5";
      nodes.push(<p key={`h-${i}`} className={cls}>{renderInline(hMatch[2])}</p>);
      i++;
      continue;
    }

    // Emoji section header
    if (startsWithEmoji(trimmed) && trimmed.length < 80 && !trimmed.includes("|")) {
      const nextT = lines[i + 1]?.trim() ?? "";
      const isHeader = nextT === "" || /^[-*\d]/.test(nextT) || startsWithEmoji(nextT);
      if (isHeader) {
        nodes.push(
          <p key={`eh-${i}`} className="text-[13.5px] font-bold text-slate-800 mt-4 mb-1.5">
            {renderInline(trimmed)}
          </p>
        );
        i++;
        continue;
      }
    }

    // Table — strict: only treat as table when next line is separator or is also a table row
    if (isTableRow(trimmed)) {
      const next = lines[i + 1]?.trim() ?? "";
      const nextIsReal = /^\|[-| :]+\|$/.test(next) || isTableRow(next);
      if (nextIsReal) {
        const tableRows: string[][] = [];
        while (
          i < lines.length &&
          (isTableRow(lines[i].trim()) || /^\|[-| :]+\|$/.test(lines[i].trim()))
        ) {
          const row = lines[i].trim();
          i++;
          if (/^\|[-| :]+\|$/.test(row)) continue;
          const cells = row.split("|").slice(1, -1).map((c) => c.trim());
          if (cells.length > 0) tableRows.push(cells);
        }
        if (tableRows.length > 0) {
          const [head, ...body] = tableRows;
          nodes.push(
            <div key={`tbl-${i}`} className="my-3 overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-[12.5px] border-collapse">
                <thead className="bg-slate-50">
                  <tr>
                    {head.map((cell, ci) => (
                      <th key={ci} className="px-3 py-2 text-left font-semibold text-slate-700 border-b border-slate-200 whitespace-nowrap">
                        {renderInline(cell)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {body.map((row, ri) => (
                    <tr key={ri} className={ri % 2 === 0 ? "bg-white" : "bg-slate-50/60"}>
                      {row.map((cell, ci) => (
                        <td key={ci} className="px-3 py-2 text-slate-600 border-b border-slate-100">
                          {renderInline(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        continue;
      }
      // Not a real table — fall through to paragraph
    }

    // Bullet list
    if (/^[-*]\s/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*]\s+/, ""));
        i++;
      }
      nodes.push(
        <ul key={`ul-${i}`} className="my-2 flex flex-col gap-1.5 pl-1">
          {items.map((it, idx) => (
            <li key={idx} className="flex gap-2 text-[13.5px] leading-relaxed">
              <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
              <span>{renderInline(it)}</span>
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Numbered list
    if (/^\d+[.)]\s/.test(trimmed)) {
      const items: string[] = [];
      let num = 1;
      while (i < lines.length && /^\d+[.)]\s/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+[.)]\s+/, ""));
        i++;
      }
      nodes.push(
        <ol key={`ol-${i}`} className="my-2 flex flex-col gap-1.5 pl-1">
          {items.map((it, idx) => (
            <li key={idx} className="flex gap-2 text-[13.5px] leading-relaxed">
              <span className="flex-shrink-0 text-indigo-500 font-semibold text-[12px] min-w-[18px]">{num++}.</span>
              <span>{renderInline(it)}</span>
            </li>
          ))}
        </ol>
      );
      continue;
    }

    // Emoji list group
    if (startsWithEmoji(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && startsWithEmoji(lines[i].trim()) && lines[i].trim().length > 0) {
        items.push(lines[i].trim());
        i++;
      }
      if (items.length > 1) {
        nodes.push(
          <ul key={`emojiul-${i}`} className="my-2 flex flex-col gap-1.5">
            {items.map((it, idx) => (
              <li key={idx} className="text-[13.5px] leading-relaxed">{renderInline(it)}</li>
            ))}
          </ul>
        );
        continue;
      }
      nodes.push(
        <p key={`ep-${i}`} className="text-[13.5px] leading-relaxed text-slate-700">
          {renderInline(trimmed)}
        </p>
      );
      i++;
      continue;
    }

    // Normal paragraph
    nodes.push(
      <p key={`p-${i}`} className="text-[13.5px] leading-relaxed text-slate-700">
        {renderInline(trimmed)}
      </p>
    );
    i++;
  }

  return (
    <div className="break-words space-y-0.5">
      {nodes}
      {streaming && <StreamCursor />}
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button
      onClick={copy}
      title="Copy response"
      className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 border border-transparent hover:border-indigo-100"
    >
      {copied ? (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
        </svg>
      )}
      {copied ? "Copied!" : "Copy"}
    </button>
  );
}

function MessageBubble({ msg }: { msg: Message }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex gap-3 group ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      <div
        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shadow-sm select-none text-white"
        style={{
          background: isUser
            ? "linear-gradient(135deg,#6366f1,#4f46e5)"
            : "linear-gradient(135deg,#6366f1,#8b5cf6)",
        }}
      >
        {isUser ? "U" : "AI"}
      </div>

      <div
        className={`max-w-[78%] flex flex-col gap-1 ${
          isUser ? "items-end" : "items-start"
        }`}
      >
        {msg.fileName && (
          <span className="text-[10px] tracking-widest uppercase font-semibold text-indigo-400 flex items-center gap-1">
            📎 {msg.fileName}
          </span>
        )}
        <div
          className={`px-4 py-3 rounded-2xl shadow-sm ${
            isUser
              ? "text-white rounded-tr-sm text-[13.5px] leading-relaxed whitespace-pre-wrap break-words"
              : "bg-white border border-slate-200 text-slate-700 rounded-tl-sm"
          }`}
          style={
            isUser ? { background: "linear-gradient(135deg,#6366f1,#4f46e5)" } : {}
          }
        >
          {isUser ? (
            <>
              {msg.content}
              {msg.streaming && <StreamCursor />}
            </>
          ) : (
            <MarkdownContent content={msg.content} streaming={msg.streaming} />
          )}
        </div>
        {!msg.streaming && (
          <div
            className={`flex items-center gap-2 ${
              isUser ? "self-end" : "self-start"
            }`}
          >
            <span className="text-[10px] text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity">
              {fmtTime(msg.timestamp)}
            </span>
            {!isUser && <CopyButton text={msg.content} />}
          </div>
        )}
      </div>
    </div>
  );
}

function FileChip({
  file,
  onRemove,
}: {
  file: AttachedFile;
  onRemove: () => void;
}) {
  return (
    <div className="inline-flex items-center gap-1.5 bg-indigo-50 border border-indigo-200 rounded-full px-3 py-1 text-xs max-w-[260px]">
      <span>{file.isImage ? "🖼️" : "📄"}</span>
      <span className="truncate text-indigo-700 font-medium">{file.name}</span>
      <span className="text-slate-400 flex-shrink-0">{fmtBytes(file.size)}</span>
      <button
        onClick={onRemove}
        className="ml-0.5 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
        aria-label="Remove file"
      >
        ✕
      </button>
    </div>
  );
}

export default function OxyClaude() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [waitingForFirst, setWaitingForFirst] = useState(false);
  const [attachedFile, setAttachedFile] = useState<AttachedFile | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useStream, setUseStream] = useState(true);
  const [selectedModel, setSelectedModel] = useState(ANTHROPIC_MODELS[0].id);
  const [modelOpen, setModelOpen] = useState(false);

  const bottomRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const userScrolledUp = useRef(false);

  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    const onScroll = () => {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
      userScrolledUp.current = !atBottom;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!userScrolledUp.current) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isLoading, waitingForFirst]);

  const scrollToBottom = () => {
    userScrolledUp.current = false;
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [input]);

  useEffect(() => {
    const id = "stream-blink";
    if (!document.getElementById(id)) {
      const style = document.createElement("style");
      style.id = id;
      style.textContent = `@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }`;
      document.head.appendChild(style);
    }
  }, []);

  const handleFile = useCallback((file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError("Only PDF and image files (JPEG, PNG, GIF, WebP) are supported.");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setError("File must be under 20 MB.");
      return;
    }
    setAttachedFile({
      raw: file,
      name: file.name,
      size: file.size,
      isImage: file.type.startsWith("image/"),
    });
    setError(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleStop = () => abortRef.current?.abort();

  const handleNewChat = () => {
    abortRef.current?.abort();
    setMessages([]);
    setInput("");
    setAttachedFile(null);
    setError(null);
    setIsLoading(false);
    setWaitingForFirst(false);
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text && !attachedFile) return;
    if (isLoading) return;

    const userMsg: Message = {
      id: uid(),
      role: "user",
      content: text || "Please analyse this file.",
      fileName: attachedFile?.name,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    scrollToBottom();
    setIsLoading(true);
    setWaitingForFirst(true);
    setError(null);

    const fileSnap = attachedFile;
    setAttachedFile(null);

    const assistantId = uid();
    const ac = new AbortController();
    abortRef.current = ac;

    const onToken = (token: string) => {
      setWaitingForFirst((prev) => {
        if (prev) {
          setMessages((msgs) => [
            ...msgs,
            {
              id: assistantId,
              role: "assistant",
              content: token,
              timestamp: new Date(),
              streaming: true,
            },
          ]);
          return false;
        }
        setMessages((msgs) =>
          msgs.map((m) =>
            m.id === assistantId ? { ...m, content: m.content + token } : m
          )
        );
        return false;
      });
    };

    const history = [
      ...messages
        .filter((m) => !m.streaming)
        .map((m) => ({ role: m.role, content: m.content })),
      { role: "user", content: userMsg.content },
    ];

    try {
      if (fileSnap) {
        await streamChatWithFile(
          fileSnap.raw,
          userMsg.content,
          selectedModel,
          onToken,
          ac.signal
        );
      } else if (useStream) {
        await streamChat(history, selectedModel, onToken, ac.signal);
      } else {
        // Non-streaming fallback
        const res = await fetch(
          `${CHAT_URL}?model=${encodeURIComponent(selectedModel)}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(history),
          }
        );
        if (!res.ok) throw new Error(`Server error ${res.status}`);
        const reader = res.body!.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        let full = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const lines = buf.split("\n");
          buf = lines.pop() ?? "";
          for (const line of lines) {
            if (!line.startsWith("data:")) continue;
            const raw = line.slice(5).trimStart();
            const token = extractToken(raw);
            if (token) full += token;
          }
        }
        setMessages((prev) => [
          ...prev,
          {
            id: assistantId,
            role: "assistant",
            content: full,
            timestamp: new Date(),
            streaming: false,
          },
        ]);
        setWaitingForFirst(false);
      }
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        const msg = err instanceof Error ? err.message : "";
        setError(
          msg.includes("500")
            ? "Unable to get a response from this model. Please try again later or switch to a different model."
            : msg || "Something went wrong."
        );
        setMessages((prev) => prev.filter((m) => m.id !== assistantId));
      }
    } finally {
      setWaitingForFirst(false);
      setMessages((prev) =>
        prev.some((m) => m.id === assistantId)
          ? prev.map((m) =>
              m.id === assistantId ? { ...m, streaming: false } : m
            )
          : prev
      );
      setIsLoading(false);
      abortRef.current = null;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isEmpty = messages.length === 0;
  const suggestions = [
    "Summarise a document",
    "Explain a concept",
    "Write a code snippet",
    "Translate text",
  ];

  return (
    <div
      className="flex flex-col h-screen overflow-hidden text-slate-900"
      style={{
        fontFamily: "'Inter','Segoe UI',system-ui,sans-serif",
        background:
          "linear-gradient(135deg,#f0f4ff 0%,#faf5ff 50%,#f0f9ff 100%)",
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      {/* Dot grid */}
      <div
        className="fixed inset-0 pointer-events-none"
        aria-hidden
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px,rgba(99,102,241,0.07) 1px,transparent 0)",
          backgroundSize: "28px 28px",
        }}
      />

      {/* Top accent bar */}
      <div
        className="fixed top-0 inset-x-0 h-[3px] pointer-events-none z-20"
        style={{ background: "linear-gradient(90deg,#6366f1,#8b5cf6,#a78bfa)" }}
      />

      {/* ── Header ── */}
      <header
        className="relative z-10 flex-shrink-0 backdrop-blur-md border-b border-white/60 shadow-sm"
        style={{ background: "rgba(255,255,255,0.85)" }}
      >
        <div className="max-w-3xl mx-auto px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-base font-bold shadow-md"
              style={{ background: "linear-gradient(135deg,#6366f1,#8b5cf6)" }}
            >
              ◈
            </div>
            <div>
              <h1
                className="text-[15px] font-extrabold tracking-tight leading-none"
                style={{
                  background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                OXY GPT
              </h1>
              <p className="text-[9px] tracking-[0.18em] uppercase text-slate-400 font-medium mt-0.5">
                Intelligent · Precise · Aware
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleNewChat}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-slate-200 bg-white text-slate-600 hover:border-indigo-300 hover:text-indigo-600 transition-all shadow-sm"
            >
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              New Chat
            </button>
            <button
              onClick={() => setUseStream((v) => !v)}
              title={useStream ? "Streaming ON" : "Streaming OFF"}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all
                ${
                  useStream
                    ? "bg-indigo-50 border-indigo-200 text-indigo-700"
                    : "bg-slate-100 border-slate-200 text-slate-500"
                }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  useStream ? "bg-indigo-500 animate-pulse" : "bg-slate-400"
                }`}
              />
              {useStream ? "Stream ON" : "Stream OFF"}
            </button>
            <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[11px] text-emerald-700 font-semibold uppercase tracking-wide">
                Live
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* ── Messages ── */}
      <main
        ref={mainRef}
        className="flex-1 overflow-y-auto relative z-10 min-h-0"
      >
        <div className="max-w-3xl mx-auto px-5 py-6 flex flex-col gap-4">
          {isEmpty && (
            <div className="flex flex-col items-center justify-center py-20 gap-6 text-center">
              <div
                className="w-20 h-20 rounded-3xl flex items-center justify-center text-white text-3xl shadow-xl"
                style={{ background: "linear-gradient(135deg,#6366f1,#8b5cf6)" }}
              >
                ◈
              </div>
              <div className="max-w-sm">
                <h2 className="text-2xl font-bold text-slate-800 mb-2">
                  How can I help you?
                </h2>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Send a message or upload a PDF / image. Drag &amp; drop
                  anywhere on the page.
                </p>
              </div>
              <div className="flex gap-2 flex-wrap justify-center">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => setInput(s)}
                    className="px-4 py-2 text-xs font-medium border border-slate-200 bg-white text-slate-500 rounded-full hover:border-indigo-300 hover:text-indigo-600 hover:bg-indigo-50 transition-all shadow-sm"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg) => (
            <MessageBubble key={msg.id} msg={msg} />
          ))}

          {waitingForFirst && <ThinkingLoader />}

          {isLoading && !useStream && !waitingForFirst && (
            <div className="flex gap-3 items-start">
              <div
                className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md"
                style={{ background: "linear-gradient(135deg,#6366f1,#8b5cf6)" }}
              >
                AI
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex items-center gap-1.5">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className="w-2 h-2 rounded-full bg-indigo-400 animate-bounce"
                    style={{ animationDelay: `${i * 0.15}s` }}
                  />
                ))}
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>
      </main>

      {/* Drag overlay */}
      {dragOver && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm"
          style={{
            background: "rgba(255,255,255,0.92)",
            border: "2px dashed #6366f1",
          }}
        >
          <div className="text-center">
            <div className="text-6xl mb-4">📂</div>
            <p className="text-indigo-600 text-xl font-bold">
              Drop your file here
            </p>
            <p className="text-slate-500 text-sm mt-1">
              PDF or image · max 20 MB
            </p>
          </div>
        </div>
      )}

      {/* ── Footer ── */}
      <footer
        className="relative z-10 flex-shrink-0 backdrop-blur-md border-t border-white/60 shadow-[0_-4px_24px_rgba(99,102,241,0.06)]"
        style={{ background: "rgba(255,255,255,0.9)" }}
      >
        <div className="max-w-3xl mx-auto px-5 py-3 flex flex-col gap-2">
          {error && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-xl text-red-600 text-xs">
              <span>⚠</span>
              <span className="flex-1">{error}</span>
              <button
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600 transition-colors"
              >
                ✕
              </button>
            </div>
          )}

          {attachedFile && (
            <FileChip
              file={attachedFile}
              onRemove={() => setAttachedFile(null)}
            />
          )}

          <div className="flex items-end gap-2">
            {/* Model selector */}
            <div className="relative flex-shrink-0">
              <button
                onClick={() => setModelOpen((v) => !v)}
                className="flex items-center gap-1.5 px-2.5 py-2.5 rounded-2xl text-[11px] font-semibold border border-slate-200 bg-white text-slate-600 hover:border-indigo-300 hover:text-indigo-600 transition-all shadow-sm"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-violet-500 flex-shrink-0" />
                {ANTHROPIC_MODELS.find((m) => m.id === selectedModel)?.label ??
                  selectedModel}
                <svg
                  width="9"
                  height="9"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {modelOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setModelOpen(false)}
                  />
                  <div className="absolute left-0 bottom-full mb-1.5 w-52 bg-white border border-slate-200 rounded-xl shadow-lg z-50 overflow-hidden">
                    <div className="px-3 py-1.5 border-b border-slate-100">
                      <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                        Select Model
                      </span>
                    </div>
                    {ANTHROPIC_MODELS.map((m) => (
                      <button
                        key={m.id}
                        onClick={() => {
                          setSelectedModel(m.id);
                          setModelOpen(false);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2 text-left text-[12px] hover:bg-indigo-50 transition-colors
                          ${
                            selectedModel === m.id
                              ? "bg-indigo-50 text-indigo-700 font-semibold"
                              : "text-slate-700"
                          }`}
                      >
                        <span>{m.label}</span>
                        <span className="text-[10px] text-slate-400">
                          {m.desc}
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Input box */}
            <div
              className={`flex-1 flex items-end gap-2 bg-white rounded-2xl px-3 py-2.5 transition-all duration-200 shadow-sm
                ${
                  dragOver
                    ? "ring-2 ring-indigo-400"
                    : "ring-1 ring-slate-200 focus-within:ring-2 focus-within:ring-indigo-400 focus-within:shadow-md focus-within:shadow-indigo-50"
                }`}
            >
              <button
                onClick={() => fileRef.current?.click()}
                title="Upload PDF or image"
                className="flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center mb-0.5 text-slate-400 hover:text-indigo-500 hover:bg-indigo-50 transition-all"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                >
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </button>
              <input
                ref={fileRef}
                type="file"
                accept={ACCEPTED_TYPES.join(",")}
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                  e.target.value = "";
                }}
              />

              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  attachedFile ? "Ask about this file…" : "Message OXY GPT…"
                }
                rows={1}
                disabled={isLoading}
                className="flex-1 bg-transparent resize-none outline-none text-slate-800 text-[13.5px] placeholder-slate-400 leading-relaxed py-0.5 disabled:opacity-50"
                style={{ minHeight: "22px", maxHeight: "120px" }}
              />

              {isLoading && useStream && (
                <button
                  onClick={handleStop}
                  title="Stop generation"
                  className="flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center mb-0.5 bg-red-50 text-red-500 hover:bg-red-100 transition-all border border-red-200"
                >
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <rect x="4" y="4" width="16" height="16" rx="2" />
                  </svg>
                </button>
              )}

              {(!isLoading || !useStream) && (
                <button
                  onClick={handleSend}
                  disabled={isLoading || (!input.trim() && !attachedFile)}
                  className={`flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center mb-0.5 transition-all duration-200
                    ${
                      isLoading || (!input.trim() && !attachedFile)
                        ? "bg-slate-100 text-slate-300 cursor-not-allowed"
                        : "text-white hover:opacity-90 active:scale-95 shadow-md"
                    }`}
                  style={
                    isLoading || (!input.trim() && !attachedFile)
                      ? {}
                      : { background: "linear-gradient(135deg,#6366f1,#8b5cf6)" }
                  }
                >
                  {isLoading ? (
                    <svg
                      className="animate-spin w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                      <path d="M12 2a10 10 0 0110 10" />
                    </svg>
                  ) : (
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                    >
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}